import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { ReCAPTCHA } from 'react-google-recaptcha';
import { FormError, Stack } from '@99designs/ui';

interface CaptchaProps {
    onChange: (token: string) => void;
    onErrored?: () => void;
    siteKey: string;
    error?: string;
}

declare global {
    interface Window {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        grecaptcha: any;
    }
}

// Set height of reCAPTCHA to avoid content visually shifting
const Placeholder = styled.div`
    --captcha-height: 78px;
    height: var(--captcha-height);
`;

export const Captcha = React.forwardRef(function Captcha(
    { onChange, onErrored, siteKey, error, ...rest }: CaptchaProps,
    ref: React.RefObject<ReCAPTCHA>
) {
    const [grecaptcha, setGrecaptcha] = useState(undefined);

    const handleLoad = () => {
        window.grecaptcha.ready(() => {
            setGrecaptcha(window.grecaptcha);
        });
    };

    useEffect(() => {
        if (!window.grecaptcha) {
            const script = document.createElement('script');
            script.type = 'text/javascript';
            script.src = 'https://www.google.com/recaptcha/api.js?render=explicit';
            script.async = true;
            script.defer = true;
            script.onload = handleLoad;

            if (onErrored) {
                script.onerror = onErrored;
            }

            document.body.appendChild(script);
        } else {
            setGrecaptcha(window.grecaptcha);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (!grecaptcha) {
        return <Placeholder aria-hidden="true" />;
    }

    return (
        <Stack space="space7">
            <ReCAPTCHA
                aria-describedby="captcha-error"
                ref={ref}
                hl={__LANG__}
                sitekey={siteKey}
                onChange={onChange}
                grecaptcha={grecaptcha}
                {...rest}
            />
            {!!error && <FormError id="captcha-error">{error}</FormError>}
        </Stack>
    );
});
